import { useState } from 'react';
import { Github, ExternalLink, X } from 'lucide-react';
import { useInView } from '../hooks/useInView';
import { projects } from '../data/portfolioData';
import { BiLogoPlayStore } from 'react-icons/bi';

export default function Projects() {
  const [ref, inView] = useInView(0.1);
  const [selected, setSelected] = useState(null);
  const [imgErrors, setImgErrors] = useState({});
  return (
    <section id="projects" style={{ background: 'var(--bg2)' }} ref={ref}>
      <div className="max-w-5xl mx-auto px-6 lg:px-16 py-24">
        <div className={`reveal ${inView ? 'visible' : ''}`}>
          <div className="section-eyebrow">04 — What I've built</div>
        </div>
        <h2
          className={`reveal ${inView ? 'visible' : ''} delay-1`}
          style={{ fontSize: 'clamp(2rem,5vw,3.2rem)', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '0.75rem' }}
        >
          Projects
        </h2>
        <p
          className={`reveal ${inView ? 'visible' : ''} delay-2`}
          style={{ color: 'var(--text2)', fontSize: '1.05rem', maxWidth: 520, lineHeight: 1.7, marginBottom: '3.5rem' }}
        >
          A selection of work spanning web apps, automation, and research. Click any card to expand.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {projects.map((proj, i) => (
            <div
              key={proj.title}
              className={`reveal ${inView ? 'visible' : ''} delay-${Math.min(i + 1, 8)}`}
              style={{
                borderRadius: 16, border: '1px solid var(--border)', background: 'var(--card)',
                overflow: 'hidden', transition: 'all 0.35s', cursor: 'none',
                display: 'flex', flexDirection: 'column',
              }}
              onClick={() => setSelected(proj)}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(124,109,250,0.45)';
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,0.45)';
                const overlay = e.currentTarget.querySelector('.proj-overlay');
                if (overlay) overlay.style.opacity = '1';
                const links = e.currentTarget.querySelector('.proj-links');
                if (links) links.style.opacity = '1';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = '';
                const overlay = e.currentTarget.querySelector('.proj-overlay');
                if (overlay) overlay.style.opacity = '0';
                const links = e.currentTarget.querySelector('.proj-links');
                if (links) links.style.opacity = '0';
              }}
            >
              {/* Real project image */}
              <div style={{ height: 185, position: 'relative', overflow: 'hidden', background: 'var(--bg3)' }}>
                {!imgErrors[proj.title] ? (
                  <img
                    src={proj?.image}
                    alt={proj?.title}
                    onError={(e) => {
                      setImgErrors(prev => ({
                        ...prev,
                        [proj.title]: true
                      }));
                    }}
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "transform 0.5s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.06)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "";
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "var(--bg3)",
                      fontFamily: "'Space Mono', monospace",
                      fontSize: "3.5rem",
                      fontWeight: 700,
                    }}
                  >
                    <span className="gradient-text">
                      {proj.title
                        .trim()
                        .split(/\s+/)
                        .map(word => word[0])
                        .join("")
                        .toUpperCase()}
                    </span>
                  </div>
                )}
                {/* <img
                  src={proj.image}
                  alt={proj.title}
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
                  onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.06)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = '')}
                /> */}
                {/* Hover gradient overlay */}
                <div
                  className="proj-overlay"
                  style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(to bottom, transparent 30%, rgba(10,10,15,0.88) 100%)',
                    opacity: 0, transition: 'opacity 0.3s',
                  }}
                />
                {/* Link buttons (top-right) */}
                <div
                  className="proj-links"
                  style={{
                    position: 'absolute', top: '0.75rem', right: '0.75rem',
                    display: 'flex', gap: '0.4rem',
                    opacity: 0, transition: 'opacity 0.3s', zIndex: 2,
                  }}
                >
                  {proj.github && (
                    <a
                      href={proj.github} target="_blank" rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      aria-label="GitHub"
                      style={{
                        width: 34, height: 34, borderRadius: '50%',
                        background: 'rgba(10,10,15,0.85)', border: '1px solid var(--border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--text)', textDecoration: 'none', transition: 'all 0.2s', cursor: 'none',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent)'; e.currentTarget.style.borderColor = 'var(--accent)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(10,10,15,0.85)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                    >
                      <Github size={14} />
                    </a>
                  )}
                  {proj.demo && (
                    <a
                      href={proj.demo} target="_blank" rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      aria-label="Live Demo"
                      style={{
                        width: 34, height: 34, borderRadius: '50%',
                        background: 'rgba(10,10,15,0.85)', border: '1px solid var(--border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--text)', textDecoration: 'none', transition: 'all 0.2s', cursor: 'none',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent2)'; e.currentTarget.style.borderColor = 'var(--accent2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(10,10,15,0.85)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                  {proj?.playStore && (
                    <a
                      href={proj.playStore} target="_blank" rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      aria-label="Play Store"
                      style={{
                        width: 34, height: 34, borderRadius: '50%',
                        background: 'rgba(10,10,15,0.85)', border: '1px solid var(--border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--text)', textDecoration: 'none', transition: 'all 0.2s', cursor: 'none',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent2)'; e.currentTarget.style.borderColor = 'var(--accent2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(10,10,15,0.85)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                    >
                      <BiLogoPlayStore size={14} />
                    </a>
                  )}
                </div>
              </div>

              {/* Card body */}
              <div style={{ padding: '1.2rem 1.4rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {proj.tags.map(t => (
                    <span key={t} style={{
                      fontSize: '0.68rem', padding: '0.18rem 0.55rem', borderRadius: '100px',
                      background: 'rgba(109,250,213,0.08)', border: '1px solid rgba(109,250,213,0.18)',
                      color: 'var(--accent3)', fontFamily: "'Space Mono', monospace",
                    }}>{t}</span>
                  ))}
                </div>
                <div style={{ fontSize: '0.97rem', fontWeight: 600, lineHeight: 1.35 }}>{proj.title}</div>
                <p style={{ fontSize: '0.83rem', color: 'var(--text2)', lineHeight: 1.65, flex: 1 }}>{proj.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Modal ── */}
      {selected && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 500,
            background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1.5rem', cursor: 'none',
          }}
          onClick={() => setSelected(null)}
        >
          <div
            style={{
              background: 'var(--bg2)', borderRadius: 20,
              border: '1px solid var(--border)',
              maxWidth: 680, width: '100%', overflow: 'hidden',
              maxHeight: '90vh', overflowY: 'auto',
              animation: 'modal-in 0.3s cubic-bezier(0.16,1,0.3,1)',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal image */}
            <div style={{ height: 280, position: 'relative', overflow: 'hidden' }}>
              {!imgErrors[selected.title] ? (
                <img
                  src={selected.image}
                  alt={selected.title}
                  onError={() => {
                    setImgErrors(prev => ({
                      ...prev,
                      [selected.title]: true
                    }));
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover"
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "var(--bg3)",
                    fontFamily: "'Space Mono', monospace",
                    fontSize: "5rem",
                    fontWeight: 700,
                  }}
                >
                  <span className="gradient-text">
                    {selected.title
                      .trim()
                      .split(/\s+/)
                      .map(word => word[0])
                      .join("")
                      .toUpperCase()}
                  </span>
                </div>
              )}
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(to bottom, transparent 50%, var(--bg2) 100%)',
              }} />
              {/* Close btn */}
              <button
                onClick={() => setSelected(null)}
                style={{
                  position: 'absolute', top: '1rem', right: '1rem',
                  width: 36, height: 36, borderRadius: '50%',
                  background: 'rgba(10,10,15,0.8)', border: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text)', cursor: 'none', transition: 'all 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--accent)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(10,10,15,0.8)'}
                aria-label="Close modal"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal content */}
            <div style={{ padding: '1.5rem 2rem 2rem' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
                {selected.tags.map(t => (
                  <span key={t} style={{
                    fontSize: '0.7rem', padding: '0.2rem 0.6rem', borderRadius: '100px',
                    background: 'rgba(109,250,213,0.08)', border: '1px solid rgba(109,250,213,0.18)',
                    color: 'var(--accent3)', fontFamily: "'Space Mono', monospace",
                  }}>{t}</span>
                ))}
              </div>
              <h3 style={{ fontSize: '1.4rem', fontWeight: 700, marginBottom: '0.75rem', letterSpacing: '-0.01em' }}>
                {selected.title}
              </h3>
              <p style={{ color: 'var(--text2)', lineHeight: 1.75, fontSize: '0.95rem', marginBottom: '1.5rem' }}>
                {selected.description}
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {selected.github && (
                  <a href={selected.github} target="_blank" rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.65rem 1.4rem', borderRadius: '100px', fontSize: '0.88rem', fontWeight: 600,
                      background: 'var(--card)', border: '1px solid var(--border)',
                      color: 'var(--text)', textDecoration: 'none', cursor: 'none', transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text)'; }}
                  >
                    <Github size={16} /> GitHub
                  </a>
                )}
                {selected.demo && (
                  <a href={selected.demo} target="_blank" rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.65rem 1.4rem', borderRadius: '100px', fontSize: '0.88rem', fontWeight: 600,
                      background: 'linear-gradient(135deg, var(--accent), var(--accent2))',
                      color: 'white', textDecoration: 'none', border: 'none', cursor: 'none',
                      boxShadow: '0 0 20px rgba(124,109,250,0.3)', transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 40px rgba(124,109,250,0.5)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = '0 0 20px rgba(124,109,250,0.3)'}
                  >
                    <ExternalLink size={16} /> Live Demo
                  </a>
                )}
                {selected?.playStore && (
                  <a href={selected.playStore} target="_blank" rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.65rem 1.4rem', borderRadius: '100px', fontSize: '0.88rem', fontWeight: 600,
                      background: 'linear-gradient(135deg, var(--accent), var(--accent2))',
                      color: 'white', textDecoration: 'none', border: 'none', cursor: 'none',
                      boxShadow: '0 0 20px rgba(124,109,250,0.3)', transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 40px rgba(124,109,250,0.5)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = '0 0 20px rgba(124,109,250,0.3)'}
                  >
                    <BiLogoPlayStore size={16} /> Play Store
                  </a>
                )}
              </div>
            </div>
          </div>
          <style>{`@keyframes modal-in { from { opacity:0; transform:scale(0.94) translateY(20px); } to { opacity:1; transform:scale(1) translateY(0); } }`}</style>
        </div>
      )}
    </section>
  );
}
